import type { MaskedTextBoxEvent } from "@tql/ui-components-react/kendo/inputs";
import {
	Input,
	NumericTextBox,
	RadioGroup,
	TextArea,
} from "@tql/ui-components-react/kendo/inputs";
import { Error, Hint, Label } from "@tql/ui-components-react/kendo/labels";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import { MaskedTextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { format, isValid, parseISO } from "date-fns";
import { useCallback, useState } from "react";
import { useParams } from "react-router";
import { z } from "zod";

import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";

import type { AugmentedShipment } from "../../types/augmented-shipment";
import * as styles from "./claims-modal.module.css";
import { useClaimsFieldIds } from "./hooks/use-claims-field-ids.ts";
import { useGetClaimReasons } from "./hooks/use-get-claim-reasons.ts";
import { usePostNewClaim } from "./hooks/use-post-new-claim.ts";
import {
	CLAIM_DETAILS_MAX_LENGTH,
	initFormState,
	yesNoOptions,
} from "./utils/constants.ts";

const emailSchema = z.string().email();

const initialErrors = {
	claimReason: false,
	claimExceedPayAmount: false,
	salvageAmount: false,
	estimatedClaimAmount: false,
	actualClaimAmount: false,
	contactEmail: false,
};

const formatLoadDate = (date: string | null | undefined): string => {
	if (!date) {
		return "N/A";
	}
	const parsed = parseISO(date);
	return isValid(parsed) ? format(parsed, "MM/dd/yyyy") : "N/A";
};

export const ClaimsModal = ({
	onClose,
	isOpen,
	shipment,
}: {
	onClose: () => void;
	isOpen: boolean;
	shipment: AugmentedShipment;
}) => {
	const params = useParams();
	const ids = useClaimsFieldIds();
	const { postNewClaimAsync, isPostingNewClaim } = usePostNewClaim();
	const { claimReasons, isLoading: isLoadingClaimReasons } =
		useGetClaimReasons();

	const claimReasonErrorId = `${ids.claimReason}_error`;
	const claimExceedPayAmountErrorId = `${ids.claimExceedPayAmount}_error`;
	const salvageAmountErrorId = `${ids.salvageAmount}_error`;
	const estimatedClaimAmountErrorId = `${ids.estimatedClaimAmount}_error`;
	const actualClaimAmountErrorId = `${ids.actualClaimAmount}_error`;
	const contactEmailErrorId = `${ids.contactEmail}_error`;
	const claimDetailsCountId = `${ids.claimDetails}_count`;

	const buildInitialFormData = useCallback(
		() => ({
			poNumber: params["poNumber"] ?? "",
			customerName: shipment?.customer?.customerName ?? "",
			carrier: shipment?.carrier,
			// `actual` is only set once the load is marked loaded/delivered; `scheduled` carries the load summary's LoadDate/DeliveryDate.
			loadDate:
				shipment?.shipmentDetails?.pickup?.actual ??
				shipment?.shipmentDetails?.pickup?.scheduled,
			deliveryDate:
				shipment?.shipmentDetails?.delivery?.actual ??
				shipment?.shipmentDetails?.delivery?.scheduled,
			...initFormState,
		}),
		[params, shipment],
	);

	const [errors, setErrors] = useState(initialErrors);
	const [formData, setFormData] = useState(buildInitialFormData);

	const handleChange = (name: string, value: string | number) => {
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
		if (name in errors) {
			setErrors((prev) => ({
				...prev,
				[name]: false,
			}));
		}
	};

	const resetForm = () => {
		setFormData(buildInitialFormData());
		setErrors(initialErrors);
	};

	const handleClose = () => {
		resetForm();
		onClose();
	};

	const handleConfirm = async () => {
		const nextErrors = {
			claimReason: !formData.claimReason,
			claimExceedPayAmount: formData.claimExceedPayAmount === "",
			salvageAmount: formData.salvageAmount < 0,
			estimatedClaimAmount: formData.estimatedClaimAmount < 0,
			actualClaimAmount: formData.actualClaimAmount < 0,
			contactEmail: !emailSchema.safeParse(formData.contactEmail.trim())
				.success,
		};

		if (Object.values(nextErrors).some(Boolean)) {
			setErrors(nextErrors);
			return;
		}

		try {
			await postNewClaimAsync(formData);
		} catch {
			return;
		}

		resetForm();
		onClose();
	};

	if (!isOpen) {
		return null;
	}

	return (
		<TraxModal
			className={styles.claimsModal}
			confirmDisabled={isPostingNewClaim}
			confirmFirst={true}
			confirmText={"Save"}
			onCancel={handleClose}
			onClose={handleClose}
			onConfirm={() => void handleConfirm()}
			title={"Claim Information"}
			type={"confirmation"}
			width={773}
		>
			<LoadingSpinner isVisible={isPostingNewClaim} />
			<div className={styles.columns}>
				<div className={styles.column}>
					<section className={styles.section}>
						<h3 className={styles.sectionHeading}>Load Information</h3>
						<div className={styles.loadInfo}>
							<div className={styles.infoItem}>
								<span className={styles.infoLabel}>PO Number</span>
								<span data-testid={"claims-po-number"}>
									{formData.poNumber || "N/A"}
								</span>
							</div>
							<div className={styles.infoItem}>
								<span className={styles.infoLabel}>Load Date</span>
								<span data-testid={"claims-load-date"}>
									{formatLoadDate(formData.loadDate)}
								</span>
							</div>
							<div className={styles.infoItem}>
								<span className={styles.infoLabel}>Delivery Date</span>
								<span data-testid={"claims-delivery-date"}>
									{formatLoadDate(formData.deliveryDate)}
								</span>
							</div>
						</div>
					</section>

					<section className={styles.section}>
						<h3 className={styles.sectionHeading}>Customer Information</h3>
						<span>{formData.customerName || "N/A"}</span>
					</section>

					<section className={styles.section}>
						<h3 className={styles.sectionHeading}>Carrier Information</h3>
						<span>{formData.carrier?.carrierName || "N/A"}</span>
					</section>

					<section className={styles.section}>
						<h3 className={styles.sectionHeading}>Claim Information</h3>
						<p className={styles.note}>
							NOTE: The claims contact is often different than your sales/
							transportation contact with your customer.
						</p>
						<div className={styles.claimFields}>
							<div className={styles.fieldPair}>
								<div className={styles.field}>
									<Label id={`${ids.needHelpWithSalvage}_label`}>
										I need help with salvage.
									</Label>
									<RadioGroup
										ariaLabelledBy={`${ids.needHelpWithSalvage}_label`}
										data={yesNoOptions}
										layout={"horizontal"}
										name={"needHelpWithSalvage"}
										onChange={(event: { value: string }) =>
											handleChange("needHelpWithSalvage", event.value)
										}
										value={formData.needHelpWithSalvage}
									/>
								</div>
								<div className={styles.field}>
									<Label editorId={ids.salvageAmount}>Salvage Amount</Label>
									<NumericTextBox
										ariaDescribedBy={
											errors.salvageAmount ? salvageAmountErrorId : undefined
										}
										format={"c2"}
										id={ids.salvageAmount}
										min={0}
										onChange={({ value }: { value: number | null }) =>
											handleChange("salvageAmount", value ?? 0)
										}
										spinners={false}
										valid={!errors.salvageAmount}
										value={formData.salvageAmount}
									/>
									{errors.salvageAmount ? (
										<Error id={salvageAmountErrorId}>
											Salvage amount must be at least 0.
										</Error>
									) : null}
								</div>
							</div>

							<div className={styles.fieldPair}>
								<div className={styles.field}>
									<Label editorId={ids.estimatedClaimAmount}>
										Estimated Claim Amount
									</Label>
									<NumericTextBox
										ariaDescribedBy={
											errors.estimatedClaimAmount
												? estimatedClaimAmountErrorId
												: undefined
										}
										format={"c2"}
										id={ids.estimatedClaimAmount}
										min={0}
										onChange={({ value }: { value: number | null }) =>
											handleChange("estimatedClaimAmount", value ?? 0)
										}
										spinners={false}
										valid={!errors.estimatedClaimAmount}
										value={formData.estimatedClaimAmount}
									/>
									{errors.estimatedClaimAmount ? (
										<Error id={estimatedClaimAmountErrorId}>
											Estimated claim amount must be at least 0.
										</Error>
									) : null}
								</div>
								<div className={styles.field}>
									<Label editorId={ids.actualClaimAmount}>
										Actual Claim Amount
									</Label>
									<NumericTextBox
										ariaDescribedBy={
											errors.actualClaimAmount
												? actualClaimAmountErrorId
												: undefined
										}
										format={"c2"}
										id={ids.actualClaimAmount}
										min={0}
										onChange={({ value }: { value: number | null }) =>
											handleChange("actualClaimAmount", value ?? 0)
										}
										spinners={false}
										valid={!errors.actualClaimAmount}
										value={formData.actualClaimAmount}
									/>
									{errors.actualClaimAmount ? (
										<Error id={actualClaimAmountErrorId}>
											Actual claim amount must be at least 0.
										</Error>
									) : null}
								</div>
							</div>

							<div className={styles.field}>
								<Label
									editorId={ids.claimReason}
									id={`${ids.claimReason}_label`}
								>
									Claim Reason
									<span className={"required"}>&nbsp;*</span>
								</Label>
								<DropDownList
									ariaDescribedBy={
										errors.claimReason ? claimReasonErrorId : undefined
									}
									ariaLabelledBy={`${ids.claimReason}_label`}
									data={claimReasons}
									dataItemKey={"id"}
									defaultItem={{ id: 0, name: "Claim Reason" }}
									id={ids.claimReason}
									loading={isLoadingClaimReasons}
									onChange={(e: { value: { id: string } }) =>
										handleChange("claimReason", e.value?.id)
									}
									textField={"name"}
									valid={!errors.claimReason}
									value={
										claimReasons.find(
											(reason) => reason.id === formData.claimReason,
										) ?? null
									}
								/>
								{errors.claimReason ? (
									<Error id={claimReasonErrorId}>
										Claim reason is required.
									</Error>
								) : null}
							</div>

							<div className={styles.fieldPair}>
								<div className={styles.field}>
									<Label id={`${ids.tempRecorder}_label`}>
										Is there a temp recorder available?
									</Label>
									<RadioGroup
										ariaLabelledBy={`${ids.tempRecorder}_label`}
										data={yesNoOptions}
										layout={"horizontal"}
										name={"tempRecorder"}
										onChange={(e: { value: string }) =>
											handleChange("tempRecorder", e.value)
										}
										value={formData.tempRecorder}
									/>
								</div>
								<div className={styles.field}>
									<Label id={`${ids.fedInspection}_label`}>
										If this is a temperature issue produce load, has a Federal
										Inspection been completed?
									</Label>
									<RadioGroup
										ariaLabelledBy={`${ids.fedInspection}_label`}
										data={yesNoOptions}
										layout={"horizontal"}
										name={"fedInspection"}
										onChange={(e: { value: string }) =>
											handleChange("fedInspection", e.value)
										}
										value={formData.fedInspection}
									/>
								</div>
							</div>
						</div>
					</section>
				</div>

				<div className={styles.column}>
					<section className={styles.section}>
						<h3 className={styles.sectionHeading}>
							Administration
							<span className={"required"}>&nbsp;*</span>
						</h3>
						<p className={styles.note} id={`${ids.claimExceedPayAmount}_label`}>
							Does the estimated value of the claim exceed the pay amount to the
							truck (less any comchecks given)?
						</p>
						<RadioGroup
							ariaDescribedBy={
								errors.claimExceedPayAmount
									? claimExceedPayAmountErrorId
									: undefined
							}
							ariaLabelledBy={`${ids.claimExceedPayAmount}_label`}
							data={yesNoOptions}
							disabled={true}
							layout={"horizontal"}
							name={"claimExceedPayAmount"}
							valid={!errors.claimExceedPayAmount}
							value={formData.claimExceedPayAmount}
						/>
						{errors.claimExceedPayAmount ? (
							<Error id={claimExceedPayAmountErrorId}>Select Yes or No.</Error>
						) : null}
					</section>

					<section className={styles.section}>
						<h3 className={styles.sectionHeading}>
							Customer Claims Contact Information
						</h3>
						<p className={styles.note}>
							NOTE: The claims contact is often different than your sales/
							transportation contact with your customer.
						</p>
						<div className={styles.contactFields}>
							<div className={`${styles.field} ${styles.contactName}`}>
								<Label editorId={ids.contactName}>Contact Name</Label>
								<Input
									id={ids.contactName}
									name={"contactName"}
									onChange={(e: { value: string }) =>
										handleChange("contactName", e.value)
									}
									value={formData.contactName}
								/>
							</div>

							<div className={`${styles.field} ${styles.contactEmail}`}>
								<Label editorId={ids.contactEmail}>
									Email
									<span className={"required"}>&nbsp;*</span>
								</Label>
								<Input
									ariaDescribedBy={
										errors.contactEmail ? contactEmailErrorId : undefined
									}
									id={ids.contactEmail}
									name={"contactEmail"}
									onChange={(e: { value: string }) =>
										handleChange("contactEmail", e.value)
									}
									type={"email"}
									valid={!errors.contactEmail}
									value={formData.contactEmail}
								/>
								{errors.contactEmail ? (
									<Error id={contactEmailErrorId}>
										Enter a valid email address.
									</Error>
								) : null}
							</div>

							<div className={styles.phoneRow}>
								<div className={`${styles.field} ${styles.contactPhone}`}>
									<Label editorId={ids.contactPhone}>Phone</Label>
									<MaskedTextBox
										id={ids.contactPhone}
										mask={"(000) 000-0000"}
										name={"contactPhone"}
										onChange={(e: MaskedTextBoxEvent) =>
											handleChange("contactPhone", e.target.value)
										}
										placeholder={"(___) ___-____"}
										value={formData.contactPhone}
									/>
								</div>
								<div className={`${styles.field} ${styles.contactExtension}`}>
									<Label editorId={ids.contactExtension}>EXT</Label>
									<Input
										id={ids.contactExtension}
										name={"contactExtension"}
										onChange={(e: { value: string }) =>
											handleChange("contactExtension", e.value)
										}
										value={formData.contactExtension}
									/>
								</div>
							</div>
						</div>

						<div className={`${styles.field} ${styles.claimDetails}`}>
							<Label editorId={ids.claimDetails}>
								Claims Details & Description
							</Label>
							<TextArea
								ariaDescribedBy={claimDetailsCountId}
								className={styles.claimDetailsInput}
								id={ids.claimDetails}
								maxLength={CLAIM_DETAILS_MAX_LENGTH}
								name={"claimDetails"}
								onChange={(e: { value: string }) =>
									handleChange("claimDetails", e.value)
								}
								value={formData.claimDetails}
							/>
							<Hint
								className={styles.claimDetailsCount}
								direction={"end"}
								id={claimDetailsCountId}
							>
								{formData.claimDetails.length}/{CLAIM_DETAILS_MAX_LENGTH}
							</Hint>
						</div>
					</section>
				</div>
			</div>
		</TraxModal>
	);
};
